











































































































































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import List from '@/components/util/List.vue';
import BuildingDisplayComponent from '@/components/system/BuildingDisplayComponent.vue';
import StartStopTimer from '@/components/orders/StartStopTimer.vue';
import WorkOrderSetComponent from '@/components/orders/WorkOrderSetComponent.vue';
import WorkerDetail from '@/components/orders/worker/detail.vue';

import { ColumnDisplayOption } from '@/models/ColumnDisplayOption';
import { SortChangedEvent } from '@/models/SortChangedEvent';

import {
  WorkordersApi,
  Workorder,
  PerformoSettings,
  QueryWorkOrdersRequest,
  Property,
  WorkorderPagedCollectionApiResponse
} from 'performo-api';
import { mapGetters } from 'vuex';
import { User } from 'oidc-client';
import { handleApiError } from '@/ApiHelpers';

@Component({
  components: {
    List,
    BuildingDisplayComponent,
    StartStopTimer,
    WorkOrderSetComponent,
    WorkerDetail
  },
  computed: mapGetters({
    currentPropertyId: 'currentPropertyId',
    currentUser: 'currentUser',
    api: 'ordersApi',
    settings: 'settings',
    currentProperty: 'currentProperty'
  })
})
export default class WorkOrderListView extends Vue {
  @Prop({ required: true }) ordersQuery!: QueryWorkOrdersRequest;
  @Prop({ required: false }) columnDisplay!: ColumnDisplayOption[];
  @Prop({ required: false }) outputMode!: string;
  @Prop({ required: false }) highlightedSetOrder!: Workorder;
  @Prop({ required: false, default: false, type: Boolean })
  hideSetComponent?: boolean;
  @Prop({ required: false, default: 'No work orders to display.' })
  zeroCountDisplayMessage!: string;
  @Prop({ required: false, type: Number, default: 25 }) pageSize!: number;

  // when true, the work order list is in "Master Detail Mode"
  // used to navigate between orders in the found set within the detail view
  // order detail and the order list found set can be toggled without losing context
  @Prop({ required: false, type: Boolean, default: false })
  masterDetailMode?: boolean;

  // true - show order list view. false - show order detail view
  private showList!: boolean;

  private api!: WorkordersApi;
  private settings!: PerformoSettings;
  private currentProperty!: Property;

  private currentPropertyId!: string;
  private currentUser!: User;

  private pagedOrders!: WorkorderPagedCollectionApiResponse;

  private loadingOrders = false;

  private pageNumber!: number;

  private currentOrder!: Workorder;
  private forbidden = false;

  data() {
    return {
      pagedOrders: undefined,
      currentOrder: undefined,
      showList: undefined
    };
  }

  get columnDisplayComputed(): ColumnDisplayOption[] {
    // if we have a columnDisplay prop, use that, its overriding the system default for a reason.
    if (this.columnDisplay) {
      return this.columnDisplay;
    }
    // finally if we have a system column set, convert to column option and use that.
    const systemColumnDisplay = this.settings
      .workOrderListColumnOptions as ColumnDisplayOption[];
    if (systemColumnDisplay && systemColumnDisplay.length) {
      return systemColumnDisplay;
    }
    // finally use the default column options for the component.
    return [
      {
        displayName: '',
        columnName: 'orderSetComponent',
        width: '25px',
        dontSort: true
      },
      { displayName: 'Order #', columnName: 'orderNumber', width: '105px' },
      {
        displayName: this.settings.multiPropLabelSingular || 'Property',
        columnName: 'propertyId',
        width: '75px'
      },
      { displayName: 'Assign To', columnName: 'assignTo' },
      {
        displayName: 'Start Date',
        columnName: 'startDateTime',
        formatKind: 1,
        width: '125px'
      },
      {
        displayName: 'Completion Date',
        columnName: 'completionDateTime',
        formatKind: 1,
        width: '140px'
      },
      {
        displayName: this.currentProperty.location1Label || 'Building',
        columnName: 'building',
        width: '175px'
      },
      {
        displayName: this.currentProperty.location2Label || 'Room',
        columnName: 'suite',
        width: '95px'
      },
      { displayName: 'Priority', columnName: 'jobPriority', width: '105px' },
      { displayName: 'Status', columnName: 'orderStatus', width: '100px' },
      {
        displayName: 'Timer',
        columnName: 'laborTimerAction',
        width: '100px',
        dontSort: true
      }
    ];
  }

  get orders() {
    if (this.pagedOrders && this.pagedOrders.data) {
      return this.pagedOrders.data;
    }
    return undefined;
  }

  private sortChangedHandler(sort: SortChangedEvent) {
    this.$emit('sort-changed', sort);
  }

  private orderStatusClass(item: Workorder) {
    if (item.orderNumber === 'REQ') {
      return 'submitted';
    } else {
      if (item.orderStatus) {
        return item.orderStatus.toLowerCase().replace(' ', '');
      }
      return '';
    }
  }

  private async paginationChangeHandler(pageNumber: number) {
    let paginationSkip = this.pageSize * (pageNumber - 1) + 1;
    this.$emit('pagination-skip-updated', paginationSkip);
  }

  private async listRowClickedHandler(item: Workorder) {
    if (this.masterDetailMode && item.workorderId) {
      this.showList = false;
      this.$emit('show-list', this.showList);
      await this.loadOrderForMasterDetailAsync(item);
    } else {
      this.$emit('workorder-clicked', item);
    }
  }

  // load the desired order details when in master detail mode
  // update the route query with the orderId
  private async loadOrderForMasterDetailAsync(order: Workorder) {
    if (order && order.workorderId) {
      // the desired order was emitted up from Found Set Navigation
      // get that order
      this.api
        .getWorkOrder({
          workorderId: order.workorderId
        })
        .then((result) => (this.currentOrder = result))
        .catch(this.errorHandler);
    } else if (this.$route.query.orderId) {
      // the current order is undefined because the browser was refreshed or some other means
      // derive and load the current order using the URL workorderId
      this.api
        .getWorkOrder({
          workorderId: this.$route.query.orderId as string
        })
        .then((result) => (this.currentOrder = result))
        .catch(this.errorHandler);
    }

    // check if order is still in current found set and handle accordingly
    if (this.pagedOrders.data) {
      const index = this.pagedOrders.data.findIndex(
        (o) => o.workorderId === order.workorderId
      );
      if (index >= 0) {
        this.$emit('master-detail-order-loaded', order.workorderId);
      } else {
        // something on the current order changed to take it out of the current found set
        // route to the main detail view of the current order
        await this.$router.push({
          name: 'work-order-detail',
          params: { id: this.$route.query.orderId as string }
        });
      }
    }
  }

  private async refresh(data?: Workorder) {
    if (data) {
      this.currentOrder = data;
    } else {
      await this.fetchOrdersAsync();
      await this.loadOrderForMasterDetailAsync(this.currentOrder);
    }
  }

  private errorHandler(error: Response) {
    if (error instanceof Response) {
      if (error.status === 403) {
        this.forbidden = true;
      }
    }
  }

  // if there is an orderId present in the route query, display the order detail view
  // otherwise, display the list view
  private setMasterDetailModeFromQuery() {
    if (this.$route.query.orderId && this.masterDetailMode) {
      this.showList = false;
    } else {
      this.showList = true;
    }
    this.$emit('show-list', this.showList);
  }

  // Returns a function, that, as long as it continues to be invoked, will not
  // be triggered. The function will be called after it stops being called for
  // N milliseconds. If `immediate` is passed, trigger the function on the
  // leading edge, instead of the trailing.
  // https://davidwalsh.name/javascript-debounce-function
  // https://stackoverflow.com/a/24004942
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  debounce(func: { (): void; apply?: any }, wait: number | undefined) {
    var timeout: number | null | undefined;
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      var context = this,
        args = arguments;
      var later = function () {
        timeout = null;
      };
      var callNow = !timeout;
      if (timeout !== null) {
        clearTimeout(timeout);
      }
      timeout = setTimeout(later, wait);
      if (callNow) func.apply(context, args);
    };
  }

  @Watch('ordersQuery', { deep: true })
  private async queryChanged() {
    await this.fetchOrdersAsync();
  }

  private fetchOrdersAsync = this.debounce(async () => {
    await this.fetchOrdersRawAsync();
  }, 25);

  private async fetchOrdersRawAsync() {
    if (this.ordersQuery) {
      const query = {
        ...this.ordersQuery,
        propertyId: this.ordersQuery.propertyId || this.currentPropertyId,
        skip: this.ordersQuery.skip,
        take: this.ordersQuery.take ? this.ordersQuery.take : this.pageSize,
        sortColumn:
          this.ordersQuery.sortColumn !== '' ? this.ordersQuery.sortColumn : '',
        sortDirection:
          this.ordersQuery.sortDirection !== ''
            ? this.ordersQuery.sortDirection
            : 'ascend'
      };
      try {
        this.pagedOrders = await this.api.queryWorkOrders(query);
      } catch (error) {
        await handleApiError(error);
      }
      this.$emit('updated-paged-orders', this.pagedOrders);
    }
  }

  async mounted() {
    await this.setModeAndLoadData();
  }

  @Watch('$route.query.orderId')
  async orderIdChangedInRoute() {
    // this is necessary for handling the back button
    // detail (back) => list does not retrigger mounted hooks
    // but the orderId is removed from the route, so we watch that
    await this.setModeAndLoadData();
  }

  private async setModeAndLoadData() {
    // determine if we are in list or detail mode
    this.setMasterDetailModeFromQuery();

    // this must be loaded before the specific order, so that found set navigation
    // component can find the index of the order. Maybe in future rewrite that
    // component to handle calculating that when the data is loaded in, instead of requiring it first.
    await this.fetchOrdersAsync();

    // if there is an orderId in the route, we load the full order
    if (this.$route.query.orderId) {
      this.api
        .getWorkOrder({
          workorderId: this.$route.query.orderId as string
        })
        .then((result) => (this.currentOrder = result))
        .catch(this.errorHandler);
    }
  }

  private userHasInterface(interfaceModule: string): boolean {
    if (!this.currentUser.profile) {
      return false;
    } // bail out if we can't read the profile object
    if (Array.isArray(this.currentUser.profile.interface)) {
      return this.currentUser.profile.interface.includes(interfaceModule);
    }
    return this.currentUser.profile.interface === interfaceModule;
  }
}
